// Actual Spend (per SAP) modal. SPEC §6.8 (formerly "Budget Actual").
//
// Big USD total at top = the full signed sum of every SAP posting this quarter
// (matched + not-yet-matched), per the data-model spec where every SAP row
// "hits Budget Actual". Per-category breakdown rows below roll the match
// categories into display groups that SUM EXACTLY to the headline — including
// the "no linked invoice" bucket, which used to be hidden and made the rows
// not add up.

import React, { useEffect, useState, useCallback } from 'react';
import { TOKENS, UI_FONT, NUM_STYLE, laneColor, CATEGORY_HINTS } from './tokens';
import { Modal, Mono, MoneyCell, Icon } from './primitives';
import { fetchBudgetActual } from './api';

// Map server match-category enum to a display row.
// Display groups, keyed to match the backend's compute_actual_spend output.
const CATEGORY_GROUPS = [
  { key: 'vendor_invoice', label: 'Vendor invoices — matched', hue: 222 },
  { key: 'unmatched', label: 'In SAP — no linked invoice', hue: 70 },
  { key: 'intercompany', label: 'Intercompany', hue: 260 },
  { key: 'clearing', label: 'Clearings', hue: 35 },
  { key: 'accrual', label: 'Accrual reversals', hue: 290 },
  { key: 'other', label: 'Other (unrecognized)', hue: 200 },
];

export default function BudgetModal({ period, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchBudgetActual(period, { breakdown: 'category' });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setData({ period, total_usd: 0, breakdown: [] });
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load Budget Actual');
      }
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  // Roll category-level rows into the 4 display groups.
  const grouped = (() => {
    if (!data?.breakdown) return [];
    const byKey = {};
    for (const b of data.breakdown) byKey[b.key] = b;
    return CATEGORY_GROUPS.map((g) => ({
      ...g,
      amount: Number(byKey[g.key]?.amount_usd || 0),
      count: Number(byKey[g.key]?.count || 0),
    }));
  })();

  return (
    <Modal
      title={`Actual Spend (per SAP) · ${period}`}
      subtitle="SAP Quarterly Posting in USD"
      onClose={onClose}
      wide
    >
      {loading && (
        <div style={{ padding: '24px 0', textAlign: 'center', color: TOKENS.ink3 }}>
          Loading…
        </div>
      )}
      {error && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 7,
            background: 'oklch(0.96 0.04 25)',
            border: `1px solid ${TOKENS.danger}`,
            color: TOKENS.danger,
            fontFamily: UI_FONT,
            fontSize: 11.5,
          }}
        >
          {error}
        </div>
      )}
      {!loading && !error && data && (
        <>
          <div>
            <div
              style={{
                ...NUM_STYLE,
                fontSize: 30,
                fontWeight: 500,
                letterSpacing: '-0.02em',
                color: Number(data.total_usd) < 0 ? TOKENS.danger : TOKENS.ink,
              }}
            >
              ${Math.abs(Number(data.total_usd || 0)).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              {Number(data.total_usd) < 0 && (
                <span style={{ marginLeft: 6, fontSize: 16, color: TOKENS.danger }}>negative</span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: TOKENS.ink3, marginTop: 4 }}>
              Everything SAP posted this quarter, in USD — the actual spend.
              Postings not yet matched to an invoice <b>still count</b> (SAP is
              the source of truth). Clearings and accrual reversals are credits
              that net the total down.
            </div>
          </div>

          <div
            style={{
              marginTop: 18,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {grouped.map((g) => (
              <div
                key={g.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr 130px 140px',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background: laneColor(g.hue, 0.62, 0.14),
                  }}
                />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontFamily: UI_FONT, fontSize: 12 }}>{g.label}</span>
                  {CATEGORY_HINTS[g.key] && (
                    <span
                      title={CATEGORY_HINTS[g.key]}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: TOKENS.ink3,
                        cursor: 'help',
                      }}
                    >
                      <Icon d="M12 16v-4m0-4h.01M12 21a9 9 0 100-18 9 9 0 000 18z" size={13} sw={1.8} />
                    </span>
                  )}
                </span>
                <Mono size={11} color={TOKENS.ink3}>
                  {g.count.toLocaleString()} match{g.count === 1 ? '' : 'es'}
                </Mono>
                <MoneyCell
                  value={g.amount}
                  currency="USD"
                  size={13}
                  color={g.amount < 0 ? TOKENS.danger : TOKENS.ink}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}
